import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConfirmDialogService, DialogConfirmActionResult } from '@seepex/ng-base';
import { IAMService } from 'app/auth';
import { CartItem, CartItemActionService, CartItemSelectorService, CartService, CurrentCartItemAvailability, CurrentCartItemPrice } from 'app/features/cart';
import { CustomerContactEventPayload } from 'app/shared/models/customer-contact-event-payload';
import { CustomerContactService } from 'app/shared/services/customer-contact.service';
import { Async } from 'app/store';
import { Observable, Subscription } from 'rxjs';
@Component({
  selector: 'app-cart-current-list',
  templateUrl: './cart-current-list.component.html',
  styleUrls: ['./cart-current-list.component.scss'],
})
export class CartCurrentListComponent implements OnDestroy, OnInit {
  // Data properties
  /**
   * Array of grouped cart items in an array
   */
  public groupedCartItems: Array<Array<CartItem>> = [];

  /**
   * Array of cart item availabilities
   */
  public availabilities: Async<Array<CurrentCartItemAvailability>>;

  /**
   * Observable to calculate total prices cart item changes
   */
  public totalPrice$: Observable<Async<CurrentCartItemPrice[]>>;

  /**
   * Table display columns
   */
  public displayColumns = [
    'Position',
    'MaterialNumber',
    'CustomerMaterialNumber',
    'MaterialDescription',
    'Availability',
    'Quantity',
    'NetWeight',
    'NetPriceEach',
    'NetPriceTotal',
    'Actions',
  ];

  /**
   * Claim to see unit prices
   */
  private allowToSeeUnitPricesClaim = 'SPS_AllowToSeeUnitPrices';
  /**
   * Claim to block showing prices
   */
  private allowToBlockPricesClaim = 'SPS_QuoteFromDistributorOnly';
  /**
   * flag to block showing prices
   */
  public dontShowPrices: boolean;

  // subscriptions
  private cartItemsSubscription$: Subscription;
  private cartItemAvailabilitySubscription$: Subscription;

  private customerContact: CustomerContactEventPayload;
  private groupedCartItemsCache: Array<Array<CartItem>>;

  /**
   * @param iamService IAmService for claims
   * @param cartItemSelectorService CartItemSelectorService to get cart item data from the store
   * @param cartItemActionService CartItemActionService to request data from the server
   * @param localeId LOCALE_ID InjectionToken of Angular
   */
  constructor(
    private iamService: IAMService,
    private cartItemSelectorService: CartItemSelectorService,
    private cartItemActionService: CartItemActionService,
    private cartService: CartService,
    private confirmDialogSerivce: ConfirmDialogService,
    private customerContactService: CustomerContactService
  ) {
    this.iamService.ensureHasClaim(this.allowToSeeUnitPricesClaim).subscribe((hasClaim: boolean) => {
      if (hasClaim) {
        this.displayColumns.splice(8, 0, 'ListPriceEach');
      }
    });

    // get the customer contact change
    this.customerContactService.customerContactChanged().subscribe((customerContact) => {
      this.customerContact = customerContact;
      if (customerContact && this.groupedCartItems) {
        // request prices, when customer contact changed
        this.requestCartItemPrices();
      }
    });

    // get the cart items from the store
    this.cartItemsSubscription$ = this.cartItemSelectorService.getCartItems().subscribe((cartItems: Async<CartItem[]>) => {
      // group cart items by serial number
      this.groupCartItems(cartItems.data);

      if (cartItems?.data && !cartItems?.isPending) {
        // request the availabilities each time the cart items changed and the loading is finished
        this.cartItemActionService.getAvailabilities(null);

        // request cart item prices
        this.requestCartItemPrices();
      }
    });

    this.cartItemAvailabilitySubscription$ = this.cartItemSelectorService.getAvailabilities().subscribe((availabilities) => (this.availabilities = availabilities));
  }

  ngOnInit() {
    this.iamService.ensureHasClaim(this.allowToBlockPricesClaim).subscribe((hasClaim: boolean) => {
      if (hasClaim) {
        this.dontShowPrices = hasClaim;
        this.displayColumns.splice(6, 3);
      }
    });
  }

  /**
   * Angular ngFor TrackBy function for performance reasons
   */
  trackBySerialNumber(index: number, item: CartItem[]) {
    return item && item.length > 0 ? item[0].SerialNumber : index;
  }

  ngOnDestroy(): void {
    if (this.cartItemsSubscription$) {
      this.cartItemsSubscription$.unsubscribe();
    }

    if (this.cartItemAvailabilitySubscription$) {
      this.cartItemAvailabilitySubscription$.unsubscribe();
    }
  }

  /**
   * Groups the cart items
   *
   * @param cartItems cartItem array
   */
  private groupCartItems(cartItems: Array<CartItem>): void {
    if (!cartItems) {
      return;
    }

    this.groupedCartItemsCache = this.groupedCartItems;
    this.groupedCartItems = new Array<Array<CartItem>>();

    for (const cartItem of cartItems) {
      // create grouped cart items by serial number
      let group: Array<CartItem> = this.getCartItemGroupForSerialNumber(cartItem.SerialNumber);

      if (!group) {
        group = new Array<CartItem>();
        this.groupedCartItems.push(group);
      }

      group.push(cartItem);
    }

    // sort the cart groups
    this.sortCartGroups();
  }

  /**
   * Sort the Cart Groups by length first and then by serial number of first cart item
   */
  private sortCartGroups(): void {
    this.groupedCartItems.sort((a, b) => {
      // first entry
      if (a.length === 0) {
        return 1;
      }

      let sortValue = 0;

      // b length is 0 or the a has a serialnumber at the first entry
      sortValue = b.length === 0 || (a.length > 0 && Boolean(a[0].SerialNumber)) ? -1 : 0;

      // b has a serial number at the first entry
      return sortValue === 0 && b.length > 0 && Boolean(b[0].SerialNumber) ? 1 : 0;
    });
  }

  /**
   * Returns the cart items by serial number
   *
   * @param serialNumber serial number of spare part
   * @returns array of cart items
   */
  private getCartItemGroupForSerialNumber(serialNumber: string): Array<CartItem> {
    return this.groupedCartItems.find((cartItemGroup) => {
      if (cartItemGroup && cartItemGroup.length === 0) {
        return;
      }

      return cartItemGroup[0].SerialNumber === serialNumber;
    });
  }

  /**
   * Loops through all cart items and request the prices for it
   */
  private requestCartItemPrices(): void {
    // go through all group lists and groups
    this.groupedCartItems.forEach((group) => {
      group.forEach((cartItem) => {
        if (this.groupedCartItemsCache.length > 0) {
          // request price only, if quantity changed
          this.groupedCartItemsCache.forEach((g) => {
            const cachedCartItem = g.find((item) => item.Id === cartItem.Id);

            if (cachedCartItem && cachedCartItem.Quantity !== cartItem.Quantity) {
              this.requestCartItemPrice(cachedCartItem.Id);
            }
          });
        } else {
          // initial price request for each cart item
          this.requestCartItemPrice(cartItem.Id);
        }
      });
    });
  }

  /**
   * Requests price for cart item
   *
   * @param cartItemId Cartitem ID
   */
  private requestCartItemPrice(cartItemId: number): void {
    this.cartItemActionService.getCartItemPrice(cartItemId, this.customerContact?.customerNumber ?? null, this.customerContact?.salesOrganizationId ?? null);
  }

  /**
   * removes the cart item
   *
   * @param cartItem cart item to be removed
   * @param event click event
   */
  public onClickRemoveItem(cartItem: CartItem, event?: Event): void {
    // stop the event propagation after click
    if (event) {
      event.stopPropagation();
    }

    // delete the cart item
    this.cartItemActionService.deleteCartItem(cartItem.Id);
  }

  /**
   * Increases the quantity of the cart item
   *
   * @param event click event
   * @param cartItem cart item
   */
  public onIncreaseQuantity(cartItem: CartItem): void {
    // change the quantity of the cart item
    this.cartItemActionService.changeQuantityForCartItem(cartItem.Id, cartItem.Quantity + 1);
  }

  /**
   * Decreases the quantity of the cart item
   *
   * @param event click event
   * @param cartItem cart item
   */
  public onDecreaseQuantity(cartItem: CartItem): void {
    if (cartItem.Quantity > 0) {
      this.cartItemActionService.changeQuantityForCartItem(cartItem.Id, cartItem.Quantity - 1);
    }
  }

  /**
   * Resets the cart items
   */
  public resetCart(): void {
    this.confirmDialogSerivce.openConfirmDialog('CART.ACTIONS.CLEAR_CART_CONFIRM', 'CART.ACTIONS.CLEAR_CART_TITLE').subscribe((confirmDialogResult: DialogConfirmActionResult) => {
      if (confirmDialogResult?.confirmed) {
        this.cartService.removeAllCartItems().subscribe(() => {
          this.cartItemActionService.clearCart();
        });
      }
    });
  }
}
